import axios, { type AxiosError, type AxiosInstance } from "axios";
import { appConfig } from "@ir/config";

/**
 * A single error shape for the whole frontend, so screens never have to know
 * whether a failure came from axios, the network, or a dave-service problem
 * detail body.
 */
export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly details: unknown;

  constructor(
    message: string,
    options: { status?: number; code?: string; details?: unknown } = {},
  ) {
    super(message);
    this.name = "ApiError";
    this.status = options.status ?? 0;
    this.code = options.code ?? "unknown_error";
    this.details = options.details;
  }

  /** True when the caller is signed out or their session was revoked. */
  get isUnauthenticated(): boolean {
    return this.status === 401;
  }

  /** True when they are signed in but not allowed to do this. */
  get isForbidden(): boolean {
    return this.status === 403;
  }

  get isNotFound(): boolean {
    return this.status === 404;
  }
}

interface ErrorBody {
  code?: string;
  message?: string;
  error?: string;
  detail?: string;
  title?: string;
}

function messageFromBody(body: unknown, fallback: string): string {
  if (typeof body === "string" && body.trim()) return body.trim();
  if (body && typeof body === "object") {
    const b = body as ErrorBody;
    return b.message ?? b.error ?? b.detail ?? b.title ?? fallback;
  }
  return fallback;
}

export function toApiError(error: unknown, fallbackMessage: string): ApiError {
  if (error instanceof ApiError) return error;

  if (axios.isAxiosError(error)) {
    const axiosError = error as AxiosError<unknown>;

    if (axiosError.code === "ERR_CANCELED") {
      return new ApiError("Request cancelled", {
        status: 0,
        code: "cancelled",
      });
    }

    if (!axiosError.response) {
      return new ApiError(
        "We could not reach the server. Check your connection and try again.",
        { status: 0, code: "network_error", details: axiosError.message },
      );
    }

    const { status, data } = axiosError.response;
    const body = data as ErrorBody | string | undefined;
    return new ApiError(messageFromBody(body, fallbackMessage), {
      status,
      code:
        (typeof body === "object" && body?.code) ||
        `http_${status}`,
      details: data,
    });
  }

  if (error instanceof Error) {
    return new ApiError(error.message || fallbackMessage, {
      code: "unexpected_error",
      details: error,
    });
  }

  return new ApiError(fallbackMessage, { code: "unexpected_error" });
}

/** Runs a request and guarantees an ApiError on the failure path. */
export async function request<T>(
  fn: () => Promise<T>,
  fallbackMessage: string,
): Promise<T> {
  try {
    return await fn();
  } catch (error) {
    throw toApiError(error, fallbackMessage);
  }
}

/**
 * Public, unauthenticated dave-service endpoints (`GetQuote`). Reachable from
 * the customer quote screen with no credentials at all.
 */
export const publicApi: AxiosInstance = axios.create({
  baseURL: appConfig.publicApiUrl,
  timeout: 30_000,
  headers: { Accept: "application/json" },
});

/**
 * Admin endpoints. Cookie-authenticated: the session cookie is HTTP-only and
 * set by dave-service, so nothing here reads or writes a token in JS. There is
 * deliberately no API key — authorisation happens server-side per user.
 */
export const adminApi: AxiosInstance = axios.create({
  baseURL: appConfig.adminApiUrl,
  timeout: 45_000,
  withCredentials: true,
  headers: { Accept: "application/json" },
});

type UnauthenticatedHandler = () => void;

let onUnauthenticated: UnauthenticatedHandler | null = null;

/**
 * Lets the admin app react to a session that died mid-visit (disabled user,
 * expired cookie) by bouncing to the sign-in screen instead of showing a wall
 * of failed panels.
 */
export function setUnauthenticatedHandler(handler: UnauthenticatedHandler | null) {
  onUnauthenticated = handler;
}

adminApi.interceptors.response.use(
  (response) => response,
  (error: unknown) => {
    const apiError = toApiError(error, "Request failed");
    if (apiError.isUnauthenticated) onUnauthenticated?.();
    return Promise.reject(apiError);
  },
);
